import { useEffect, useState } from 'react';
import { Icon } from './Icon';
import type { IconName } from '../data';

type Tone = 'info' | 'navy' | 'orange' | 'success';

interface Step {
  icon: IconName;
  tone: Tone;
  title: string;
  sub: string;
  status: string;
  live?: boolean;
}

const STEPS: Step[] = [
  { icon: 'store',       tone: 'info',    title: 'Shopify event',     sub: 'Cart abandoned · ₹3,240 · Priya S.',          status: 'Trigger' },
  { icon: 'layers',      tone: 'navy',    title: 'Scenario matched',  sub: 'Abandoned Cart Recovery · auto-selected',    status: 'Routing' },
  { icon: 'phone',       tone: 'orange',  title: 'AI agent calling',  sub: 'Maya · +91 9xxx 21 · English (IN)',          status: 'Live', live: true },
  { icon: 'check',       tone: 'success', title: 'Outcome logged',    sub: 'Recovered · payment link sent on WhatsApp',  status: 'Done' },
];

const TONES: Record<Tone, { bg: string; fg: string }> = {
  info:    { bg: 'var(--info-bg)',    fg: 'var(--info)'      },
  navy:    { bg: 'var(--navy-50)',    fg: 'var(--navy-700)'  },
  orange:  { bg: 'var(--orange-50)',  fg: 'var(--orange-600)' },
  success: { bg: 'var(--success-bg)', fg: 'var(--success)'   },
};

const WAVE_BARS = 22;

export function HeroFlowCard() {
  const [active, setActive] = useState(0);
  const [wave, setWave] = useState<number[]>(() => Array.from({ length: WAVE_BARS }, () => 8));

  // Auto-advance through the flow every 1.9s
  useEffect(() => {
    const t = window.setInterval(() => {
      setActive((a) => (a + 1) % STEPS.length);
    }, 1900);
    return () => window.clearInterval(t);
  }, []);

  // Animate the waveform while the "calling" step is active
  useEffect(() => {
    if (active !== 2) return;
    const t = window.setInterval(() => {
      setWave((w) => w.map(() => 5 + Math.random() * 24));
    }, 150);
    return () => window.clearInterval(t);
  }, [active]);

  return (
    <div className="flow-wrap">
      {/* floating chips */}
      <div className="float-chip" style={{ top: -20, left: 14, animationDelay: '0s' }}>
        <span className="ico" style={{ background: 'var(--success-bg)', color: 'var(--success)' }}>
          <Icon name="trending-up" size={14} />
        </span>
        23% carts recovered
      </div>
      <div className="float-chip" style={{ bottom: 18, right: -18, animationDelay: '1.4s' }}>
        <span className="ico" style={{ background: 'var(--info-bg)', color: 'var(--info)' }}>
          <Icon name="phone" size={14} />
        </span>
        Answered in 1.2s
      </div>

      <div className="flow-card">
        {/* window chrome */}
        <div className="flow-head">
          <div className="teaser-dots">
            <span style={{ background: '#ff5f57' }} />
            <span style={{ background: '#febc2e' }} />
            <span style={{ background: '#28c840' }} />
          </div>
          <span className="flow-head-title">CallXcel Dashboard</span>
          <span className="flow-head-live"><span className="live-dot" /> Realtime</span>
        </div>

        {/* mini KPI strip */}
        <div className="flow-kpis">
          <div className="flow-kpi">
            <div className="flow-kpi-v">44</div>
            <div className="flow-kpi-l">Scenarios live</div>
          </div>
          <div className="flow-kpi">
            <div className="flow-kpi-v">148</div>
            <div className="flow-kpi-l">Calls today</div>
          </div>
          <div className="flow-kpi">
            <div className="flow-kpi-v success">₹4.2L</div>
            <div className="flow-kpi-l">Recovered</div>
          </div>
        </div>

        {/* the flow */}
        <div className="flow-steps">
          <div className="flow-rail">
            <div className="flow-rail-fill" style={{ height: `${(active / (STEPS.length - 1)) * 100}%` }} />
          </div>
          {STEPS.map((s, i) => {
            const t = TONES[s.tone];
            const state: 'done' | 'active' | 'idle' =
              i < active ? 'done' : i === active ? 'active' : 'idle';
            return (
              <div className={`flow-step ${state}`} key={i}>
                <div
                  className="flow-node"
                  style={state !== 'idle' ? { background: t.bg, color: t.fg, borderColor: t.fg } : undefined}
                >
                  <Icon name={i < active ? 'check' : s.icon} size={15} />
                </div>
                <div className="flow-step-body">
                  <div className="flow-step-top">
                    <span className="flow-step-title">{s.title}</span>
                    <span
                      className="flow-step-status"
                      style={state === 'active' ? { color: t.fg, background: t.bg } : undefined}
                    >
                      {s.live && state === 'active'
                        ? <span className="live-dot" style={{ width: 6, height: 6 }} />
                        : null}
                      {s.status}
                    </span>
                  </div>
                  <div className="flow-step-sub">{s.sub}</div>
                  {i === 2 && state === 'active' ? (
                    <div className="flow-wave">
                      {wave.map((h, k) => (
                        <span
                          key={k}
                          style={{
                            height: `${h}px`,
                            background: k % 3 === 0 ? 'var(--orange-500)' : 'var(--border-strong)',
                          }}
                        />
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>

        {/* live activity footer */}
        <div className="flow-foot">
          <span className="flow-foot-pill"><span className="live-dot" /> Live activity</span>
          <span>Maya recovered a ₹3,240 cart · just now</span>
        </div>
      </div>
    </div>
  );
}
